import React, { useState, useEffect, useMemo, useCallback } from "react";
import { displayDomainLabel } from "@/reports/telecom/displayDomainLabel";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAutosaveIndicator } from "@/hooks/useAutosaveIndicator";
import { SaveChip } from "@/components/ui/SaveChip";
import { AlertTriangle, Filter, ExternalLink, ChevronDown, ChevronUp, Shield } from "lucide-react";
import { EnrichedFindingsPanel } from "@/components/reviewer/EnrichedFindingsPanel";
import { resolveFindingControlLabel } from "@/lib/findingControlLabel";
import { resolveFindingFrameworkPills } from "@/lib/findingFrameworkPills";
import { FindingFrameworkPills } from "@/components/reviewer/FindingFrameworkPills";
import { OwnerMultiSelect } from "@/components/findings/OwnerMultiSelect";
import { DueDatePicker } from "@/components/findings/DueDatePicker";
import { FindingJustificationEvidence } from "@/components/reviewer/FindingJustificationEvidence";
import { dueDateSortKey, formatDueDate } from "@/lib/dueDate";
import { useQuestionOrdinals } from "@/hooks/useQuestionOrdinals";
import { resolveDisplayOrdinal, questionLabel } from "@/lib/questionOrdinals";


const sb: any = supabase as any;

interface FindingRow {
  id: string;
  display_id?: string | null;       // Phase 7 — persistent finding tracking ref (e.g. F-001)
  display_seq?: number | null;
  question_id: number;
  control_code: string;
  finding_title: string;
  priority_level: string;
  recommended_action: string;
  timeframe: string;
  recommended_measures: string;
  remediation_path: string;
  risk_level: string;
  created_at: string;
  // enriched from RPC + reviewerData
  assessment_id?: string;
  csv_domain?: string;
  nist_function?: string;
  domain?: string;
  question_title?: string;
  source_type?: string;
  source_key?: string | null;
  control_label?: string;
  nist_codes?: string[];
  display_q_number?: number | null;
  ordinal_source?: string | null;

  is_infrastructure?: boolean;
  owners?: string[];
  due_date?: string | null;
}

interface QuestionInfo {
  question_id: number;
  domain: string;
  title: string;
  controls: string[];
}

interface FindingsTabProps {
  assessmentId: string;
  disabled?: boolean;
  reviewerData: QuestionInfo[];
  onNavigateToQuestion?: (questionId: number) => void;
  /** Phase 6: enables enriched executive blocks for telecom_v2 only. */
  questionnaireKey?: string;
  /** Bumped by parent on tab switch to force a refetch (cross-tab sync). */
  reloadKey?: number;
}

const RISK_ORDER: Record<string, number> = { Critical: 1, HIGH: 2, High: 2, MODERATE: 3, Moderate: 3, LOW: 4, Low: 4 };
const RISK_COLORS: Record<string, string> = {
  Critical: "bg-red-700 text-white",
  HIGH: "bg-destructive text-destructive-foreground",
  High: "bg-destructive text-destructive-foreground",
  MODERATE: "bg-yellow-500/90 text-white",
  Moderate: "bg-yellow-500/90 text-white",
  LOW: "bg-green-600 text-white",
  Low: "bg-green-600 text-white",
};
const PRIORITY_COLORS: Record<string, string> = {
  Immediate: "bg-destructive/10 text-destructive border-destructive/30",
  "Quick Win": "bg-yellow-500/10 text-yellow-700 border-yellow-500/30",
  Strategic: "bg-blue-500/10 text-blue-700 border-blue-500/30",
};

// Priority sort weight: Immediate -> Quick Win -> Strategic -> (anything else)
const PRIORITY_ORDER: Record<string, number> = {
  Immediate: 1,
  "Quick Win": 2,
  Strategic: 3,
};

// NIST CSF function label lookup (used for fallback when csv_domain is missing)
const DOMAIN_LABELS: Record<string, string> = {
  GV: "Govern", ID: "Identify", PR: "Protect", DE: "Detect", RS: "Respond", RC: "Recover",
};

function deriveDomain(controlCode: string): string {
  if (!controlCode) return "—";
  const prefix = controlCode.split(".")[0];
  return DOMAIN_LABELS[prefix] ?? prefix;
}

/** Normalize NIST function value to a short, human-friendly label. */
function normalizeNistFunction(raw?: string | null): string {
  if (!raw) return "";
  const v = String(raw).trim();
  if (DOMAIN_LABELS[v.toUpperCase()]) return DOMAIN_LABELS[v.toUpperCase()];
  return v;
}

export const FindingsTab: React.FC<FindingsTabProps> = ({
  assessmentId,
  disabled = false,
  reviewerData,
  onNavigateToQuestion,
  questionnaireKey,
  reloadKey,
}) => {
  const [findings, setFindings] = useState<FindingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Filters
  const [filterRisk, setFilterRisk] = useState<string>("all");
  const [filterCsvDomain, setFilterCsvDomain] = useState<string>("all");
  const [filterNist, setFilterNist] = useState<string>("all");
  const [filterPriority, setFilterPriority] = useState<string>("all");

  // Due Date sort toggle. Default "asc" mirrors the RPC ordering
  // (due_date ASC nulls last). Clicking the header cycles asc → desc → asc.
  const [dueSort, setDueSort] = useState<"asc" | "desc">("asc");

  const questionMap = useMemo(() => {
    const m = new Map<number, QuestionInfo>();
    for (const q of reviewerData) m.set(q.question_id, q);
    return m;
  }, [reviewerData]);

  // Phase 2A closeout — the displayed Q-number is the frozen, per-assessment
  // display_ordinal resolved server-side (assessment_question_applicability).
  // It is NEVER derived from array index / list position: filtering a question
  // out must not renumber any other finding.
  const { data: ordinalMap } = useQuestionOrdinals(assessmentId);


  const loadFindings = useCallback(async () => {
    if (!assessmentId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const { data, error } = await sb.rpc("reviewer_get_findings", {
        p_assessment_id: assessmentId,
      });
      if (error) {
        console.warn("FindingsTab load error:", error);
        // Preserve existing findings on failure rather than silently clearing.
        setLoadError(error.message || "Failed to load findings.");
        return;
      }
      const rows: FindingRow[] = (data || []).map((f: any) => {
        const qi = questionMap.get(f.question_id);
        const nistFromRpc = normalizeNistFunction(f.nist_function);
        const nistFromQi = qi?.domain ? (DOMAIN_LABELS[qi.domain] ?? qi.domain) : "";
        const nistResolved = nistFromRpc || nistFromQi || deriveDomain(f.control_code);
        return {
          id: f.id,
          display_id: f.display_id ?? null,
          display_seq: f.display_seq ?? null,
          assessment_id: assessmentId,
          question_id: f.question_id,
          control_code: f.control_code ?? "",
          finding_title: f.finding_title ?? "",
          priority_level: f.priority_level ?? "",
          recommended_action: f.recommended_action ?? "",
          timeframe: f.timeframe ?? "",
          recommended_measures: f.recommended_measures ?? "",
          remediation_path: f.remediation_path ?? "",
          risk_level: f.risk_level ?? "",
          created_at: f.created_at,
          csv_domain: displayDomainLabel(f.csv_domain, f.source_type),
          nist_function: nistResolved,
          domain: nistResolved,
          question_title: qi?.title ?? "",
          source_type: f.source_type ?? undefined,
          source_key: f.source_key ?? f.infrastructure_control_key ?? null,
          control_label: resolveFindingControlLabel({
            source_type: f.source_type,
            source_key: f.source_key ?? f.infrastructure_control_key,
            control_code: f.control_code,
          }),
          nist_codes: Array.isArray(f.nist_codes) ? f.nist_codes : undefined,
          is_infrastructure: f.source_type === "infrastructure_security"
            || !!(f.source_key ?? f.infrastructure_control_key),
          // Server-resolved frozen ordinal wins; the client map is only a
          // secondary read of the same authoritative RPC source.
          display_q_number:
            f.display_q_number ??
            resolveDisplayOrdinal(ordinalMap, f.question_id, "FindingsTab"),
          ordinal_source: f.ordinal_source ?? ordinalMap?.get(Number(f.question_id))?.ordinal_source ?? null,

          owners: Array.isArray(f.owners) ? f.owners : [],
          due_date: f.due_date ?? null,
        };
      })
      .filter((r: FindingRow) => {
        if (r.is_infrastructure && (!r.control_label || r.control_label === "—")) {
          console.warn("[FindingsTab] hiding orphan infra finding", r.id, r.source_key);
          return false;
        }
        return true;
      });
      // Default order matches the RPC (due_date ASC, nulls last).
      // Client-side priority sort removed — due date is now the primary key.
      setFindings(rows);
    } catch (e: any) {
      console.warn("FindingsTab error:", e);
      setLoadError(e?.message || "Failed to load findings.");
    } finally {
      setLoading(false);
    }
  }, [assessmentId, questionMap, ordinalMap]);

  useEffect(() => {
    loadFindings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFindings, reloadKey]);

  // Filtered findings
  const filtered = useMemo(() => {
    const base = findings.filter((f) => {
      if (filterRisk !== "all" && f.risk_level !== filterRisk) return false;
      if (filterCsvDomain !== "all" && (f.csv_domain || "—") !== filterCsvDomain) return false;
      if (filterNist !== "all" && (f.nist_function || "—") !== filterNist) return false;
      if (filterPriority !== "all" && f.priority_level !== filterPriority) return false;
      return true;
    });
    return [...base].sort((a, b) => {
      const ta = dueDateSortKey(a.due_date);
      const tb = dueDateSortKey(b.due_date);
      if (ta === tb) return 0;
      // Nulls always last in both directions
      if (ta === Number.POSITIVE_INFINITY) return 1;
      if (tb === Number.POSITIVE_INFINITY) return -1;
      return dueSort === "asc" ? ta - tb : tb - ta;
    });
  }, [findings, filterRisk, filterCsvDomain, filterNist, filterPriority, dueSort]);

  // Summary metrics
  const metrics = useMemo(() => {
    const total = findings.length;
    const critical = findings.filter((f) => f.risk_level === "Critical").length;
    const high = findings.filter((f) => f.risk_level === "HIGH" || f.risk_level === "High").length;
    const moderate = findings.filter((f) => f.risk_level === "MODERATE" || f.risk_level === "Moderate").length;
    const low = findings.filter((f) => f.risk_level === "LOW" || f.risk_level === "Low").length;
    return { total, critical, high, moderate, low };
  }, [findings]);

  // Filter option lists — CSV Domain primary, NIST Function secondary
  const uniqueCsvDomains = useMemo(
    () => [...new Set(findings.map((f) => f.csv_domain).filter(Boolean) as string[])].sort(),
    [findings],
  );
  const uniqueNistFunctions = useMemo(
    () => [...new Set(findings.map((f) => f.nist_function).filter(Boolean) as string[])].sort(),
    [findings],
  );
  const uniquePriorities = useMemo(() => [...new Set(findings.map((f) => f.priority_level).filter(Boolean))].sort(), [findings]);
  const uniqueRisks = useMemo(() => [...new Set(findings.map((f) => f.risk_level).filter(Boolean))], [findings]);

  if (loading) {
    return (
      <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
        Loading findings…
      </div>
    );
  }

  if (loadError && findings.length === 0) {
    return (
      <div className="bg-card rounded-lg border p-8 text-center">
        <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-destructive/70" />
        <p className="font-medium text-foreground">Couldn’t load findings.</p>
        <p className="text-sm text-muted-foreground mt-1">{loadError}</p>
        <button
          type="button"
          onClick={() => loadFindings()}
          className="mt-3 inline-flex items-center rounded-md border border-input bg-background px-3 py-1.5 text-xs hover:bg-muted"
        >
          Retry
        </button>
      </div>
    );
  }

  if (findings.length === 0) {
    return (
      <div className="bg-card rounded-lg border p-8 text-center text-muted-foreground">
        <Shield className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
        <p className="font-medium">No findings recorded yet.</p>
        <p className="text-sm mt-1">Findings are created in the Assessment Results tab when a question is scored as Not Implemented or Partially Implemented.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <MetricCard label="Total Findings" value={metrics.total} className="bg-card" />
        <MetricCard label="Critical" value={metrics.critical} className="bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400" />
        <MetricCard label="High" value={metrics.high} className="bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400" />
        <MetricCard label="Moderate" value={metrics.moderate} className="bg-yellow-50 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-400" />
        <MetricCard label="Low" value={metrics.low} className="bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-card rounded-lg border p-3">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={filterRisk} onValueChange={setFilterRisk}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Risk Level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Risks</SelectItem>
            {uniqueRisks.map((r) => (
              <SelectItem key={r} value={r}>{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterCsvDomain} onValueChange={setFilterCsvDomain}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="CSV Domain" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All CSV Domains</SelectItem>
            {uniqueCsvDomains.map((d) => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterNist} onValueChange={setFilterNist}>
          <SelectTrigger className="w-[170px]">
            <SelectValue placeholder="NIST Function" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All NIST Functions</SelectItem>
            {uniqueNistFunctions.map((d) => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {uniquePriorities.map((p) => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(filterRisk !== "all" || filterCsvDomain !== "all" || filterNist !== "all" || filterPriority !== "all") && (
          <button
            className="text-xs text-primary hover:underline"
            onClick={() => {
              setFilterRisk("all");
              setFilterCsvDomain("all");
              setFilterNist("all");
              setFilterPriority("all");
            }}
          >
            Clear filters
          </button>
        )}

        <span className="ml-auto text-xs text-muted-foreground">
          Showing {filtered.length} of {findings.length}
        </span>
      </div>

      {/* Findings Table */}
      <div className="bg-card rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[110px]">Control</TableHead>
              <TableHead className="w-[230px]">CSV Domain</TableHead>
              <TableHead>Finding</TableHead>
              <TableHead className="w-[100px]">Risk</TableHead>
              <TableHead className="w-[110px]">Priority</TableHead>
              <TableHead className="w-[110px]">Timeframe</TableHead>
              <TableHead className="w-[140px]">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 hover:text-foreground"
                  onClick={() =>
                    setDueSort((s) => (s === "asc" ? "desc" : "asc"))
                  }
                  title="Sort by Due Date"
                >
                  Due Date
                  <span className="text-[10px] text-muted-foreground">
                    {dueSort === "asc" ? "▲" : "▼"}
                  </span>
                </button>
              </TableHead>
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((f) => (
              <React.Fragment key={f.id}>
                <TableRow
                  className="cursor-pointer"
                  onClick={() => setExpandedId(expandedId === f.id ? null : f.id)}
                >
                  <TableCell className={`align-top ${f.source_type === "infrastructure_security" ? "text-xs" : "font-mono text-xs"}`}>{f.control_label || f.control_code || "—"}</TableCell>
                  <TableCell className="align-top">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-semibold text-foreground leading-tight">
                        {f.csv_domain || <span className="italic text-muted-foreground font-normal">Unmapped</span>}
                      </span>
                      <FindingFrameworkPills
                        pills={resolveFindingFrameworkPills({
                          source_type: f.source_type,
                          source_key: f.source_key,
                          nist_function: f.nist_function,
                        })}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="flex items-start gap-2">
                      {f.display_id && (
                        <span
                          className="shrink-0 rounded border border-primary/30 bg-primary/5 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-primary"
                          title="Finding tracking reference (persistent within this assessment)"
                        >
                          {f.display_id}
                        </span>
                      )}
                      <span className="leading-snug">
                        {f.finding_title || <span className="italic text-muted-foreground">No title</span>}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${RISK_COLORS[f.risk_level] ?? "bg-muted text-muted-foreground"} text-[10px] px-1.5 py-0`}>
                      {f.risk_level || "—"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${PRIORITY_COLORS[f.priority_level] ?? ""}`}>
                      {f.priority_level || "—"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{f.timeframe || "—"}</TableCell>
                  <TableCell className="text-xs align-top whitespace-nowrap">
                    {f.due_date ? (
                      <span className="font-medium text-foreground">
                        {formatDueDate(f.due_date) || f.due_date}
                      </span>
                    ) : (
                      <span className="text-muted-foreground italic">Not Assigned</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {onNavigateToQuestion && (
                        <button
                          type="button"
                          title="View in Assessment Results"
                          className="p-1 text-primary hover:text-primary/80"
                          onClick={(e) => { e.stopPropagation(); onNavigateToQuestion(f.question_id); }}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {expandedId === f.id ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                    </div>
                  </TableCell>
                </TableRow>

                {expandedId === f.id && (
                  <TableRow>
                    <TableCell colSpan={8} className="bg-muted/30 p-0">
                      <FindingDetailEditor
                        finding={f}
                        disabled={disabled}
                        onUpdate={(updated) => {
                          setFindings((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
                        }}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Phase 6 — telecom_v2 only: enriched executive narrative blocks below the table */}
      {questionnaireKey === "telecom_v2" && (
        <EnrichedFindingsPanel findings={filtered as any} />
      )}
    </div>
  );
};

// --- Inline editor for an expanded finding ---
const FindingDetailEditor: React.FC<{
  finding: FindingRow;
  disabled: boolean;
  onUpdate: (updated: Partial<FindingRow> & { id: string }) => void;
}> = ({ finding, disabled, onUpdate }) => {
  const { state, begin, succeed, fail } = useAutosaveIndicator(200, 1500);

  const save = useCallback(
    async (field: string, value: string) => {
      begin();
      try {
        // Phase 2.13 — id-keyed update so findings without a question_id
        // (e.g. promoted from Technical Validation) save correctly.
        const params: Record<string, any> = { p_finding_id: finding.id };
        const fieldMap: Record<string, string> = {
          finding_title: "p_finding_title",
          priority_level: "p_priority_level",
          recommended_action: "p_recommended_action",
          timeframe: "p_timeframe",
          recommended_measures: "p_recommended_measures",
          remediation_path: "p_remediation_path",
          risk_level: "p_risk_level",
        };
        const pkey = fieldMap[field];
        if (!pkey) throw new Error(`Unsupported field: ${field}`);
        params[pkey] = value;

        const { error } = await sb.rpc("reviewer_update_finding_by_id", params);
        if (error) throw error;
        onUpdate({ id: finding.id, [field]: value });
        succeed();
      } catch (e) {
        console.error("FindingDetailEditor save error:", e);
        fail();
      }
    },
    [finding, begin, succeed, fail, onUpdate]
  );

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          {finding.display_id && (
            <span
              className="rounded border border-primary/30 bg-primary/5 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-primary"
              title="Finding tracking reference (persistent within this assessment)"
            >
              {finding.display_id}
            </span>
          )}
          <span>
            {finding.is_infrastructure ? (
              <>Infrastructure Control — {finding.control_label || finding.source_key || finding.control_code}</>
            ) : (
              <>
                {questionLabel(finding.display_q_number)}
                {" — "}
                {finding.question_title || finding.control_code}
              </>
            )}
          </span>
        </div>
        <SaveChip state={state} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Risk Level */}
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase mb-1 block">Risk Level</label>
          <Select
            value={finding.risk_level}
            onValueChange={(v) => save("risk_level", v)}
            disabled={disabled}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="HIGH">HIGH</SelectItem>
              <SelectItem value="MODERATE">MODERATE</SelectItem>
              <SelectItem value="LOW">LOW</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Priority */}
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase mb-1 block">Priority</label>
          <Select
            value={finding.priority_level}
            onValueChange={(v) => save("priority_level", v)}
            disabled={disabled}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Immediate">Immediate</SelectItem>
              <SelectItem value="Quick Win">Quick Win</SelectItem>
              <SelectItem value="Strategic">Strategic</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Timeframe */}
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase mb-1 block">Timeframe</label>
          <Select
            value={finding.timeframe}
            onValueChange={(v) => save("timeframe", v)}
            disabled={disabled}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30–60 days">30–60 days</SelectItem>
              <SelectItem value="60–90 days">60–90 days</SelectItem>
              <SelectItem value="90–120 days">90–120 days</SelectItem>
              <SelectItem value="90–180 days">90–180 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Text fields */}
      <EditableTextField
        label="Finding Title"
        value={finding.finding_title}
        disabled={disabled}
        onSave={(v) => save("finding_title", v)}
      />
      <EditableTextField
        label="Recommended Action"
        value={finding.recommended_action}
        disabled={disabled}
        multiline
        onSave={(v) => save("recommended_action", v)}
      />
      <EditableTextField
        label="Recommended Measures"
        value={finding.recommended_measures}
        disabled={disabled}
        multiline
        onSave={(v) => save("recommended_measures", v)}
      />
      <EditableTextField
        label="Remediation Path"
        value={finding.remediation_path}
        disabled={disabled}
        multiline
        onSave={(v) => save("remediation_path", v)}
      />

      {/* Phase 2.11 — Ownership */}
      <FindingOwnershipEditor
        finding={finding}
        disabled={disabled}
        onUpdated={(patch) => onUpdate({ id: finding.id, ...patch })}
      />

      {/* Phase 2 — Justification & Evidence Traceability */}
      {finding.assessment_id && (
        <FindingJustificationEvidence
          assessmentId={finding.assessment_id}
          findingId={finding.id}
          disabled={disabled}
        />
      )}
    </div>
  );
};

// --- Owners + Due Date editor (Phase 2.11) ---
const FindingOwnershipEditor: React.FC<{
  finding: FindingRow;
  disabled: boolean;
  onUpdated: (patch: Partial<FindingRow>) => void;
}> = ({ finding, disabled, onUpdated }) => {
  const { state, begin, succeed, fail } = useAutosaveIndicator(200, 1500);

  const persist = useCallback(
    async (patch: { owners?: string[]; due_date?: string | null }) => {
      begin();
      try {
        const nextOwners = patch.owners ?? finding.owners ?? [];
        const nextDue = patch.due_date !== undefined ? patch.due_date : finding.due_date ?? null;
        const { error } = await sb.rpc("reviewer_update_finding_ownership", {
          p_assessment_id: finding.assessment_id,
          p_finding_id: finding.id,
          p_owners: nextOwners,
          p_due_date: nextDue,
        });
        if (error) throw error;
        onUpdated({ owners: nextOwners, due_date: nextDue });
        succeed();
      } catch (e: any) {
        console.error("FindingOwnershipEditor save error:", e?.message || e, e);
        fail();
      }

    },
    [finding, begin, succeed, fail, onUpdated]
  );

  return (
    <div className="rounded-md border border-dashed border-border bg-muted/20 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase">Ownership</span>
        <SaveChip state={state} />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[260px]">
          <label className="text-[10px] font-medium text-muted-foreground uppercase mb-1 block">Owner(s)</label>
          <OwnerMultiSelect
            value={finding.owners ?? []}
            onChange={(next) => persist({ owners: next })}
            disabled={disabled}
          />
        </div>
        <div>
          <label className="text-[10px] font-medium text-muted-foreground uppercase mb-1 block">Due Date</label>
          <DueDatePicker
            value={finding.due_date ?? null}
            onChange={(iso) => persist({ due_date: iso })}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
};

// --- Editable text field with blur-save ---
const EditableTextField: React.FC<{
  label: string;
  value: string;
  disabled: boolean;
  multiline?: boolean;
  onSave: (value: string) => void;
}> = ({ label, value, disabled, multiline, onSave }) => {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);

  const handleBlur = () => {
    if (local !== value) onSave(local);
  };

  const cls = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground uppercase mb-1 block">{label}</label>
      {multiline ? (
        <textarea
          className={`${cls} min-h-[70px] resize-y`}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={handleBlur}
          disabled={disabled}
        />
      ) : (
        <input
          type="text"
          className={`${cls} h-9`}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={handleBlur}
          disabled={disabled}
        />
      )}
    </div>
  );
};

// --- Summary metric card ---
const MetricCard: React.FC<{ label: string; value: number; className?: string }> = ({ label, value, className = "" }) => (
  <div className={`rounded-lg border p-3 text-center ${className}`}>
    <div className="text-2xl font-bold">{value}</div>
    <div className="text-xs font-medium mt-0.5">{label}</div>
  </div>
);
